<template>
  <ScrollContainer ref="wrapperRef">
    <div ref="spinRef" v-loading="loading" :style="spinStyle" :loading-tip="loadingTip">
      <slot></slot>
    </div>
  </ScrollContainer>
</template>
<script lang="ts" setup>
  import { useMutationObserver, useThrottleFn } from '@vueuse/core';
  import { computed, nextTick, onMounted, ref, shallowRef, unref, watch } from 'vue';
  import type { CSSProperties } from 'vue';
  import { useWindowSizeFn } from '@mts-fe/hooks';
  import { ScrollContainer } from '@mts/components/Container';
  import { windowSize } from '@mts/utils';
  import { PCMode } from '@mts/utils/env';
  import { theme } from '@mts/adapter';
  import { createModalContext } from '../hooks/useModalContext';

  defineOptions({ name: 'ModalWrapper', inheritAttrs: false });

  const props = defineProps({
    loading: { type: Boolean },
    useWrapper: { type: Boolean, default: true },
    modalHeaderHeight: { type: Number, default: 50 },
    modalFooterHeight: { type: Number, default: 33 + theme.token!.controlHeight! },
    minHeight: { type: Number, default: 200 },
    height: { type: Number },
    // modalId: { type: String },
    footerOffset: { type: Number, default: 0 },
    open: { type: Boolean },
    fullScreen: { type: Boolean },
    loadingTip: { type: String },
  });

  const emit = defineEmits(['height-change', 'ext-height']);

  const wrapperRef = ref<any>(null);
  const spinRef = shallowRef<any>(null);
  const realHeightRef = ref(0);
  const minRealHeightRef = ref(0);
  const maxRealHeightRef = ref(0);

  const debounceSetHeight = useThrottleFn(setModalHeight, 300);

  PCMode && useWindowSizeFn(debounceSetHeight.bind(null));

  useMutationObserver(spinRef, () => requestAnimationFrame(debounceSetHeight), {
    attributes: true,
    subtree: true,
  });

  createModalContext({
    getModalHight: () => realHeightRef.value,
    redoModalHeight: debounceSetHeight,
  });

  const spinStyle = computed((): CSSProperties => {
    const style: CSSProperties = {
      minHeight: `${props.minHeight}px`,
    };

    if (!props.fullScreen) {
      style.maxHeight = `${unref(maxRealHeightRef)}px`;
    }
    if (props.fullScreen || props.height) {
      style.height = `${unref(realHeightRef)}px`;
    }
    return style;
  });

  watch(
    () => [props.useWrapper, props.fullScreen, props.height, props.minHeight],
    ([useWrapper, fullScreen]) => {
      if (!realHeightRef.value) {
        realHeightRef.value = props.height || 0;
      }
      if (useWrapper) {
        debounceSetHeight();
      }

      if (fullScreen === false) {
        realHeightRef.value = minRealHeightRef.value;
      } else if (fullScreen === true) {
        minRealHeightRef.value = realHeightRef.value;
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    const { modalHeaderHeight, modalFooterHeight } = props;
    emit('ext-height', modalHeaderHeight + modalFooterHeight);

    nextTick(setModalHeight);
  });

  const scrollTop = () => {
    const wrapperRefDom = unref(wrapperRef);
    if (!wrapperRefDom) return;

    const scrollTo = (wrapperRefDom as any)?.scrollTo;
    if (typeof scrollTo === 'function') {
      scrollTo.call(wrapperRefDom, 0);
    }
  };

  async function setModalHeight() {
    // props.modalId && console.log('setModalHeight', { ...props });
    if (!props.open && PCMode) return;
    const wrapperRefDom = unref(wrapperRef);
    if (!wrapperRefDom) return;

    const bodyDom = wrapperRefDom.$el?.parentElement;
    if (!bodyDom) return;

    await nextTick();

    try {
      const modalDom = bodyDom.parentElement?.parentElement;
      if (!modalDom) return;

      const modalTop = 0;
      let maxHeight =
        windowSize.height -
        modalTop * 2 +
        (props.footerOffset || 0) -
        props.modalFooterHeight -
        props.modalHeaderHeight;

      // 距离顶部过进会出现滚动条
      if (modalTop < 40) {
        maxHeight -= 26;
      }

      maxRealHeightRef.value = maxHeight;

      const spinEl = unref(spinRef);
      if (!spinEl) return;

      let finalHeight: number;
      if (props.fullScreen) {
        finalHeight = windowSize.height - props.modalFooterHeight - props.modalHeaderHeight;
      } else if (props.height) {
        finalHeight = props.height;
      } else {
        const contentHeight = spinEl.scrollHeight || 0;
        finalHeight = contentHeight > maxHeight ? maxHeight : contentHeight;
      }

      if (realHeightRef.value !== finalHeight) {
        realHeightRef.value = finalHeight;
        emit('height-change', finalHeight);
      }
    } catch (error) {
      console.error('ModalWrapper setModalHeight error:', error);
    }
  }

  defineExpose({ scrollTop, setModalHeight });
</script>
